import { OrganizationModel } from '@app/orgs/services/orgs.model';
import { OrgService } from '@app/orgs/services/org.service';
import { SearchService } from '@app/search/services/search.service';
import { SearchFiltersComponent } from '@app/search/components/search-filters/search-filters.component';
import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NUM_TILE_RESULTS_PER_PAGE } from '@app/shared/search.token';
import { AuthService } from '@app/shared/services/auth.service';
import { TranslateService } from '@ngx-translate/core';
import {
  CustomSearchTrackerProperties,
  SearchExecuted,
  SearchOrigin,
  SearchResultView,
  SearchSubmitMethod,
  SearchTrackerService,
  SearchTypeaheadInitLocations,
} from '@app/search/services/search-tracker.service';
import { DOCUMENT, Location } from '@angular/common';
import { Title } from '@angular/platform-browser';
import {
  SearchFacetValue,
  SearchFilterFilter,
  SearchSuggestionSource,
} from '@app/search/search-api.model';
import { SubscriberBaseDirective } from '@app/shared/components/subscriber-base/subscriber-base.directive';
import { DfIconChevronLeft16, DfIconRegistry } from '@lib/fresco';
import { SearchFiltersStateService } from '@app/search/components/search-filters/search-filters-state.service';
import { WindowToken } from '@app/shared/window.token';
import { ContentCardService } from '@app/shared/components/content/content-card/content-card.service';
import {
  SearchFilterId,
  SearchFiltersService,
  SearchFlagsService,
} from '@app/search/services';
import { SearchSort } from '@app/search/components/search-view';
import { WindowLayoutService } from '@dg/shared-services';
import { getSelectedFilterCount } from '@app/search/components/search-view/search-reactive-store/utils';
import { SimpleItemViewModel } from '@app/shared/models/core-view.model';

@Component({
  selector: 'dgx-search-landing-page',
  templateUrl: './search-landing-page.component.html',
  styleUrls: ['./search-landing-page.component.scss'],
})
export class SearchLandingPageComponent
  extends SubscriberBaseDirective
  implements OnInit, OnChanges, OnDestroy
{
  private _filters: any[];

  @Input() public set filters(input) {
    this._filters =
      this.showSearchLayoutRedesign &&
      !this.hideFilters &&
      this.useFilterSidebar
        ? this.searchFiltersService.transformSearchFilters(input)
        : input;
  }
  public get filters() {
    return this._filters;
  }
  @Input() public isLoading = false;
  @Input() public hasError = false;
  @Input() public title: string;
  @Input() public results: any[];
  @Input() public totalNumResults: number;
  @Input() public defaultFilterIds: string[];
  @Input() public filterIdsToIgnore: SearchFilterId[];
  @Input() public numberOfFilterSkeletonColumns: number;
  @Input() public filterIdToPersist: SearchFilterId;
  @Input() public hideOrgSelector = false;
  @Input() public hideFilters = false;
  @Input() public customLayout = false;
  @Input() public searchOrigin = SearchOrigin.search;
  @Input() public customTrackerProperties?: CustomSearchTrackerProperties;
  @Input() public suggestionSource: SearchSuggestionSource;
  @Input() public showDefaultSuggestions = false;
  @Input() public persistedFilterParam: string;
  @Input() public hasNoResultsFullPage = false;
  // whether the parent component uses the global search api `api/search/findlearningresources`
  @Input() public usesGlobalSearchApi = false;
  @Input() public showSortByWithInitialValue: string;
  @Input() public showSearchTabs = false;
  /** Temporary until all implementations of the search landing page are converted */
  @Input() public useFilterSidebar = false;

  @Output() public searchTerm = new EventEmitter();

  @ViewChild('searchFilters') public searchFilters: SearchFiltersComponent;

  public i18n = this.translate.instant([
    'LearningSearch_Popular',
    'LearningSearch_Recent',
    'LearningSearch_Relevant',
    'LearningSearch_MostPopular',
    'LearningSearch_MostRecent',
    'LearningSearch_MostRelevant',
    'Core_External',
  ]);

  public term: string;
  public pageNum: number;
  public catalogs: OrganizationModel[];
  public selectedCatalog: any;
  public isCatalogLoaded = false;
  public currentSort: SearchSort = 'recent';
  public orgId: number;
  public initiationLocation = SearchTypeaheadInitLocations.searchBar;

  private externalCatalog: Partial<OrganizationModel>;
  private orgCatalog: OrganizationModel;
  private previousPageNum: number;
  private isFiltering = false;
  private isPaging = false;
  private canNavigateBack = false;

  constructor(
    private authService: AuthService,
    private translate: TranslateService,
    private route: ActivatedRoute,
    private router: Router,
    private searchTrackerService: SearchTrackerService,
    private location: Location,
    private pageTitle: Title,
    private searchService: SearchService,
    private iconRegistry: DfIconRegistry,
    private orgService: OrgService,
    private searchFiltersStateService: SearchFiltersStateService,
    private contentCardService: ContentCardService,
    private searchFlagsService: SearchFlagsService,
    private searchFiltersService: SearchFiltersService,
    private windowLayoutService: WindowLayoutService,
    @Inject(WindowToken) private windowRef: Window,
    @Inject(DOCUMENT) private document: Document,
    @Inject(NUM_TILE_RESULTS_PER_PAGE) public readonly numResultsPerPage: number
  ) {
    super();

    this.iconRegistry.registerIcons([DfIconChevronLeft16]);
  }

  public get menuConfig() {
    return [
      {
        title: this.i18n.LearningSearch_Popular,
        defaultAction: () => this.doSort('popular'),
      },
      {
        title: this.i18n.LearningSearch_Recent,
        defaultAction: () => this.doSort('recent'),
      },
      {
        title: this.i18n.LearningSearch_Relevant,
        defaultAction: () => this.doSort('relevant'),
      },
    ];
  }

  public get showFilterReset(): boolean {
    return this.appliedFacetCount > 0;
  }

  public get showNoResults(): boolean {
    return this.hasError || (!this.isLoading && !this.totalNumResults);
  }

  public get isPhone(): boolean {
    return this.windowLayoutService.isPhone;
  }

  public get isMobile(): boolean {
    return this.windowLayoutService.isMobile;
  }

  get sortButtonText(): string {
    switch (this.currentSort) {
      case 'popular':
        return this.i18n.LearningSearch_MostPopular;
      case 'recent':
        return this.i18n.LearningSearch_MostRecent;
      case 'relevant':
        return this.i18n.LearningSearch_MostRelevant;
    }
  }

  public get showSearchInitiationAutocomplete(): boolean {
    return this.searchFlagsService.showSearchInitiationAutocomplete;
  }

  public get showSearchLayoutRedesign(): boolean {
    return this.searchFlagsService.showSearchLayoutRedesign;
  }

  public get showOrgSelector(): boolean {
    return (
      !this.hideOrgSelector && this.isCatalogLoaded && this.catalogs?.length > 1
    );
  }

  public get resultsSkeletons() {
    return new Array(this.numResultsPerPage);
  }

  // Currently sorting only applies to the /learning/provider route
  public get showSort(): boolean {
    return this.showSortByWithInitialValue !== undefined;
  }

  private get appliedFacetCount() {
    return this.hideFilters ? 0 : getSelectedFilterCount(this.filters);
  }

  public async ngOnInit(): Promise<void> {
    this.externalCatalog = {
      name: this.i18n.Core_External,
    };

    const authUser = this.authService.authUser;
    this.orgId = authUser?.defaultOrgId;

    this.searchTrackerService.setTrackerLocationSearch();

    this.canNavigateBack = this.router.navigated;

    await this.loadCatalogs(
      authUser?.defaultOrgId,
      !authUser?.defaultOrgInfo?.settings.hideExternalCatalog
    );

    this.route.queryParams.subscribe((params) => {
      this.term = params.term || '';
      this.pageNum = params.pageNum ? parseInt(params.pageNum) : 1;
      this.currentSort = params.sort || this.showSortByWithInitialValue;

      if (Boolean(params.ignorePreferredLanguage)) {
        this.searchFiltersStateService.setIgnorePreferredLanguage(true);
      }

      // Update the page title with the term
      this.updatePageTitle(this.term);

      // Calculate number of results to skip from page number
      const numResultsToSkip =
        this.pageNum * this.numResultsPerPage - this.numResultsPerPage;

      // Get whether we search the internal or external catalog
      let isExternalCat = false;
      if (params.isExternalCat && params.isExternalCat === 'true') {
        this.selectedCatalog = this.externalCatalog;
        isExternalCat = true;
      } else {
        // Not using the external catalog, if param not specified deafult to
        // the user's org catalog (set in this.loadCatalogs())

        this.selectedCatalog = !params.orgId
          ? this.orgCatalog
          : this.catalogs.find(
              (c) => c.organizationId === parseInt(params.orgId)
            );
      }

      const filters = this.searchService.getFiltersFromParam(params.filters);

      // Check if a filter ID is provided to persist
      if (this.filterIdToPersist) {
        // If so, find the filter by the ID and persist it
        const persistedFilter = filters.find(
          (filter) => filter.id === this.filterIdToPersist
        );
        this.persistedFilterParam =
          this.searchService.getParamFragFromFacet(persistedFilter);
      } else if (this.persistedFilterParam) {
        // If a persisted parameter is provided, persist it
        const [persistedFilter] = this.searchService.getFiltersFromParam(
          this.persistedFilterParam
        );

        // Check if the filter was already specified
        // If so, remove it to favor the persisted parameter provided
        if (filters.length) {
          // Find the filter by ID and remove it from the array, if it exists
          const index = filters.findIndex(
            (item) => item.id === persistedFilter.id
          );
          if (index > -1) {
            filters.splice(index, 1);
          }
        }
        // Add the persisted filter to the array
        filters.push(persistedFilter);
      }

      // Ensure we are at the top of the page
      this.document.documentElement.scrollTop = 0;

      const orgId = this.selectedCatalog?.organizationId || this.orgId;

      // Perform a search
      this.searchTerm.emit({
        term: this.term,
        filters,
        numResultsToSkip,
        isExternalCat,
        orgId: isExternalCat ? null : orgId,
        sortBy: this.currentSort,
      });
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.isLoading?.previousValue && !changes.isLoading?.currentValue) {
      if (this.isFiltering || this.isPaging) {
        if (this.isFiltering && changes.filters?.currentValue) {
          this.isFiltering = false;
          this.trackFiltering();
        } else if (this.isPaging) {
          this.isPaging = false;
          this.trackFiltering();
        }
      } else {
        this.trackSearchExecuted();
      }
    }

    if (changes.title?.currentValue) {
      // Update the page title
      this.updatePageTitle(this.term);
    }
  }

  public ngOnDestroy(): void {
    this.contentCardService.setIndexesOfExpandedCards([]);
  }

  /**
   * New search term. Reset query params and update with the new term.
   * @param term The value entered into the input field
   */
  public inputEntered(term?: string): void {
    this.searchFiltersStateService.setIgnorePreferredLanguage(false);
    this.updateRoute(
      {
        term: term || null,
        filters: this.buildFiltersParam(),
        pageNum: null,
        isExternalCat: !!this.selectedCatalog?.organizationId
          ? !this.selectedCatalog?.organizationId
          : null,
        sort: null,
      },
      true
    );
  }

  public goToPage(pageNum: number): void {
    this.previousPageNum = this.pageNum;

    // If pageNum equals the default value of one, remove it as a parameter for a cleaner URI
    pageNum = pageNum > 1 ? pageNum : null;

    this.isPaging = true;
    this.updateRoute({ pageNum }, true);
    this.contentCardService.setIndexesOfExpandedCards([]);
  }

  /** Apply new sidebar filter changes */
  public updateFilters(uiFilters: SearchFilterFilter[]) {
    const appliedFacets = this.searchService.getAppliedFilters(uiFilters);
    const filtersParam: string = appliedFacets
      .map((facet) => this.searchService.getParamFragFromFacet(facet))
      .join('|');
    this.updateRoute(this.buildQueryParams(filtersParam), true);
  }

  /** Reset new sidebar filter */
  public resetFilters() {
    this.updateRoute(this.buildQueryParams(), true);
  }

  /** Apply old header filter changes (deprecated) */
  public applyFilters(): void {
    const isSelectedFacet = ({
      isSelected,
    }: SimpleItemViewModel<SearchFacetValue>) => isSelected;

    // does the given filter have selected facets?
    const hasSelectedfacets = (filter: SearchFilterFilter) =>
      filter.subitems.some(isSelectedFacet);

    // get encoded value of facet
    // `foo bar` => `foo%20bar`
    const encodeValue = (subItem: SimpleItemViewModel<SearchFacetValue>) =>
      encodeURIComponent(subItem.model.id);

    // construct our `filter` param
    // example - 'Type:article;assessment|Location:Colorado'
    const filterQuery = this.searchFilters.filters
      .filter(hasSelectedfacets)
      .map((filter) => {
        const values = filter.subitems
          .filter(isSelectedFacet)
          .map(encodeValue)
          .join(';');

        return `${filter.config.id}:${values}`;
      })
      .join('|');

    this.isFiltering = true;

    this.updateRoute(this.buildQueryParams(filterQuery), true);
  }

  public onCatalogChange(catalog: any): void {
    if (catalog.isFirstLoad) {
      return;
    }

    this.updateRoute(
      {
        term: this.term || null,
        filters: this.buildFiltersParam(),
        pageNum: null,
        isExternalCat: !catalog.organizationId || null,
        orgId: catalog.organizationId || null,
      },
      true
    );
  }

  public backClick(event: Event): void {
    event.preventDefault();

    const trackerProperties = {
      searchTerm: this.term,
      origin: this.searchOrigin,
      pageNumber: this.pageNum,
      searchFilters: this.filters,
      linkClicked: SearchSubmitMethod.allResults,
      custom: this.customTrackerProperties,
    } as any;

    this.addTotalProperty(trackerProperties);
    this.searchTrackerService.searchResultViewChanged(trackerProperties);

    if (this.canNavigateBack && this.windowRef.history?.length > 1) {
      this.location.back();
    } else {
      this.router.navigate(['learning', 'search'], {
        queryParams: {
          term: this.term,
        },
      });
    }
  }

  public doSort(sort: SearchSort) {
    this.currentSort = sort;

    const sortType = {
      popular: SearchSubmitMethod.popular,
      recent: SearchSubmitMethod.recent,
      relevant: SearchSubmitMethod.relevant,
    }[sort];

    this.searchTrackerService.searchResultViewChanged({
      linkClicked: SearchSubmitMethod.sortBy,
      sortType,
    });

    if (sort === this.showSortByWithInitialValue) {
      sort = null;
    }

    this.updateRoute({ sort }, true);
  }

  private trackSearchExecuted() {
    const trackerProperties = {
      searchTerm: this.term,
      origin: this.searchOrigin,
      custom: this.customTrackerProperties,
      filters: this.filters,
      isExternal: !this.selectedCatalog?.organizationId,
    } as SearchExecuted;

    this.addTotalProperty(trackerProperties);
    this.searchTrackerService.searchExecuted(trackerProperties);
  }

  private trackFiltering(): void {
    const trackerProperties = {
      searchTerm: this.term,
      isExternal: !this.selectedCatalog?.organizationId,
      origin: this.searchOrigin,
      pageNumber: this.pageNum,
      previousPageNumber: this.previousPageNum,
      custom: this.customTrackerProperties,
      filters: this.filters,
    } as SearchResultView;

    this.addTotalProperty(trackerProperties);
    this.searchTrackerService.searchResultViewChanged(trackerProperties);
  }

  private addTotalProperty(trackerProperties: any) {
    switch (this.searchOrigin) {
      case SearchOrigin.groups:
        trackerProperties.groupsTotal = this.totalNumResults;
        break;
      case SearchOrigin.opportunities:
        trackerProperties.opportunitiesTotal = this.totalNumResults;
        break;
      case SearchOrigin.people:
        trackerProperties.peopleTotal = this.totalNumResults;
        break;
      case SearchOrigin.skills:
        trackerProperties.skillsTotal = this.totalNumResults;
        break;
      default:
        trackerProperties.contentTotal = this.totalNumResults;
    }
  }

  private async loadCatalogs(
    orgId: number,
    showExternalCatalog: boolean
  ): Promise<void> {
    if (this.hideOrgSelector) {
      this.isCatalogLoaded = true;
      return;
    }

    const organizations: OrganizationModel[] = await this.orgService
      .getUserOrganizations()
      .toPromise();

    if (organizations?.length > 0) {
      this.catalogs = [...organizations];

      // cache user's org catalog to use when not using the external catalog
      this.orgCatalog = organizations.find(
        (organization) => organization.organizationId === orgId
      );

      if (showExternalCatalog) {
        this.catalogs = [
          ...this.catalogs,
          this.externalCatalog as OrganizationModel,
        ];
      }
    }

    this.isCatalogLoaded = true;
  }

  private buildQueryParams(filtersParam: string = null) {
    return {
      // If all filters have been unselected, remove as a param.
      filters: this.buildFiltersParam(filtersParam) || null,
      pageNum: null,
      // ignorePreferredLanguage must be in the route in order to reset the filters
      // (since the route could be empty when trying to reset filters, the route won't trigger unless we change something in it)
      ignorePreferredLanguage:
        (this.searchFiltersStateService.extractLanguageFilter &&
          this.searchFiltersStateService.ignorePreferredLanguage) ||
        null,
    };
  }

  private updateRoute(
    queryParams = {},
    shouldMergeParams: boolean = false
  ): void {
    const queryParamsHandling = shouldMergeParams ? 'merge' : null;
    this.router.navigate([], {
      queryParams,
      queryParamsHandling,
      relativeTo: this.route,
      replaceUrl: true,
    });
  }

  public get resultsRange() {
    const rangeStart =
      this.pageNum * this.numResultsPerPage - this.numResultsPerPage + 1;
    const rangeEnd = this.getResultsRangeLimit();
    const totalNumResults = this.totalNumResults;

    return this.searchService.getSearchResltsRange(
      rangeStart,
      rangeEnd,
      totalNumResults
    );
  }

  private getResultsRangeLimit(): number {
    if (this.pageNum * this.numResultsPerPage > this.totalNumResults) {
      return this.totalNumResults;
    }

    return this.pageNum * this.numResultsPerPage;
  }

  private updatePageTitle(term?: string): void {
    let pageTitle = `${this.title || 'Degreed'} | Degreed`;
    pageTitle = term ? `${term} - ${pageTitle}` : pageTitle;
    this.pageTitle.setTitle(pageTitle);
  }

  private buildFiltersParam(filtersParam: string = null) {
    // If there is a persistedFilterParam and it is ignored, include it
    if (
      this.persistedFilterParam &&
      this.filterIdsToIgnore.includes(this.filterIdToPersist)
    ) {
      if (!filtersParam) {
        filtersParam = '';
      }
      filtersParam += this.persistedFilterParam;
    }
    if (filtersParam && filtersParam[filtersParam.length - 1] === '|') {
      filtersParam = filtersParam.substr(0, filtersParam.length - 1);
    }
    return filtersParam;
  }
}
